import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
  type Query,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  IconCart,
  IconHome,
  IconHomeFilled,
  IconOrders,
  IconProfile,
  IconSearch,
  TabBar,
  ToastProvider,
} from '@aps/ui';
import {
  canOrder,
  setAuthenticated,
  setFailed,
  setGuest,
  setInitializing,
  useSessionStatus,
} from './session.js';
import { api, setTokens, setUnauthenticatedHandler } from '../lib/api.js';
import { initData, initTelegram, telegramSession } from '../lib/telegram.js';
import { CartScreen } from '../screens/CartScreen.js';
import { CatalogScreen } from '../screens/CatalogScreen.js';
import { CategoriesScreen } from '../screens/CategoriesScreen.js';
import { SuccessScreen } from '../screens/SuccessScreen.js';
import { CheckoutScreen } from '../screens/CheckoutScreen.js';
import { HomeScreen } from '../screens/HomeScreen.js';
import { GarageScreen } from '../screens/GarageScreen.js';
import { OrderScreen } from '../screens/OrderScreen.js';
import { OrdersScreen } from '../screens/OrdersScreen.js';
import { ProductScreen } from '../screens/ProductScreen.js';
import { ProfileScreen } from '../screens/ProfileScreen.js';
import { ShipmentScreen } from '../screens/ShipmentScreen.js';
import { SearchScreen } from '../screens/SearchScreen.js';
import {
  NavigationProvider,
  ROOT_ROUTES,
  useNavigation,
  type RootRouteName,
} from './navigation.js';
import { RegionProvider } from './region.js';
import { SettingsProvider, useSettings } from './settings.js';

/**
 * Ilova qobig'i.
 *
 * Kirish AVTOMATIK: Telegram `initData` ni serverga yuboradi va token
 * oladi. Foydalanuvchidan hech narsa so'ralmaydi — TWA da "kirish"
 * ekrani ortiqcha to'siq.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Katalog tez-tez o'zgarmaydi; ortiqcha so'rov 4G da kechikish beradi.
        staleTime: 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Mobil tarmoqqa yetadigan chegara — 4G da odatiy javob 1-2 s, lekin
 * qamrov yomon joyda uzoqroq kutish kerak. Belgilangan doimiy —
 * soxta taymerlar bilan ham deterministik sinaladi.
 */
export const LOGIN_TIMEOUT_MS = 15_000;

/** Shaxsiy so'rovlar — muvaffaqiyatsiz handshake'dan keyin TOZALANADI. */
const PRIVATE_QUERY_ROOTS = new Set([
  'cart',
  'garage',
  'recommendations',
  'orders',
  'order',
  'shipment',
]);

function isPrivateQuery(query: Query): boolean {
  return PRIVATE_QUERY_ROOTS.has(String(query.queryKey[0]));
}

/**
 * Sessiya tugaganda shaxsiy keshni tozalaydi.
 *
 * Avval BEKOR qilinadi (kutilayotgan so'rov keshni qayta tiklab
 * qo'ymasligi uchun), keyin O'CHIRILADI. Ochiq endpointlar (katalog,
 * qidiruv) TEGILMAYDI — mehmon ularni ko'rishda davom etadi.
 */
async function clearPrivateQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({ predicate: isPrivateQuery });
  queryClient.removeQueries({ predicate: isPrivateQuery });
}

function Screens(): ReactNode {
  const { route } = useNavigation();

  switch (route.name) {
    case 'search':
      return <SearchScreen {...(route.query === undefined ? {} : { initialQuery: route.query })} />;
    case 'catalog':
      return (
        <CatalogScreen
          {...(route.categoryId === undefined ? {} : { categoryId: route.categoryId })}
          {...(route.title === undefined ? {} : { title: route.title })}
        />
      );
    case 'categories':
      return <CategoriesScreen />;
    case 'product':
      return <ProductScreen slug={route.slug} />;
    case 'cart':
      return <CartScreen />;
    case 'success':
      return <SuccessScreen orderId={route.orderId} number={route.number} />;
    case 'checkout':
      return <CheckoutScreen />;
    case 'orders':
      return <OrdersScreen />;
    case 'order':
      return <OrderScreen id={route.id} />;
    case 'shipment':
      return <ShipmentScreen id={route.id} />;
    case 'garage':
      return <GarageScreen />;
    case 'profile':
      return <ProfileScreen />;
    case 'home':
    default:
      return <HomeScreen />;
  }
}

interface ShellProps {
  onRetryLogin: () => void;
}

function Shell({ onRetryLogin }: ShellProps): ReactNode {
  const { t } = useSettings();
  const { route, switchRoot } = useNavigation();
  const status = useSessionStatus();

  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: api.cart,
    retry: false,
    enabled: canOrder(),
  });

  const cartCount = cart.data?.itemCount ?? 0;
  const items = ROOT_ROUTES.map((name) => ({
    id: name,
    label: t(`nav.${name}` as const),
    icon: NAV_ICON[name],
    // Faol "Asosiy" — to'ldirilgan uy (maket).
    ...(name === 'home' ? { activeIcon: <IconHomeFilled size={22} /> } : {}),
    // `exactOptionalPropertyTypes` — kalit faqat qiymat bo'lganda qo'yiladi.
    ...(name === 'cart' && cartCount > 0 ? { badge: cartCount } : {}),
  }));

  const activeRoot: RootRouteName = (ROOT_ROUTES as readonly string[]).includes(route.name)
    ? (route.name as RootRouteName)
    : 'home';

  return (
    <div className="aps-app">
      {status === 'failed' && (
        <div className="aps-row" role="alert">
          <span>{t('common.error')}</span>
          <Button variant="secondary" size="sm" onClick={onRetryLogin}>
            {t('common.retry')}
          </Button>
        </div>
      )}
      <Screens />
      <TabBar
        items={items}
        activeId={activeRoot}
        onSelect={(id) => {
          switchRoot(id as RootRouteName);
        }}
      />
    </div>
  );
}

/**
 * Pastki menyu ikonkalari.
 *
 * Komponent ICHIDA yaratilmaydi: har renderda yangi element bo'lib
 * qolardi va React butun menyuni qayta chizardi.
 */
const NAV_ICON: Record<RootRouteName, ReactNode> = {
  home: <IconHome size={22} />,
  // Maketda Katalog — lupa.
  catalog: <IconSearch size={22} />,
  cart: <IconCart size={22} />,
  orders: <IconOrders size={22} />,
  profile: <IconProfile size={22} />,
};

function Bootstrap(): ReactNode {
  const { theme } = useSettings();
  const queryClient = useQueryClient();

  /*
   * Har urinish o'z AVLODIGA ega. Kech kelgan javob (eskirgan urinish,
   * unmount qilingandan keyingi javob) faqat shu raqam ORQALI
   * aniqlanadi — StrictMode qayta o'rnatishi yoki qayta urinish eski
   * javobni jonlantirmaydi.
   */
  const generationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const abortCurrent = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const attemptLogin = useCallback(() => {
    abortCurrent();
    const generation = ++generationRef.current;
    setInitializing();

    const controller = new AbortController();
    controllerRef.current = controller;
    timeoutRef.current = setTimeout(() => {
      controller.abort();
    }, LOGIN_TIMEOUT_MS);

    void api
      .loginWithTelegram(initData(), controller.signal)
      .then((tokens) => {
        if (generationRef.current !== generation) return;
        // Avval token, KEYIN `authenticated` — boshqa joyda holat
        // token bo'lmagan holda "kirilgan" deb o'qilmasligi kerak.
        setTokens(tokens);
        setAuthenticated();
      })
      .catch(() => {
        if (generationRef.current !== generation) return;
        setTokens(null);
        void clearPrivateQueries(queryClient).then(() => {
          if (generationRef.current !== generation) return;
          setFailed();
        });
      })
      .finally(() => {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      });
  }, [abortCurrent, queryClient]);

  useEffect(() => {
    // Fon rangi TWA sarlavhasiga ham qo'yiladi — aks holda ilova
    // ustida Telegram'ning oq paneli qolib ketadi. Rang TOKENDAN
    // o'qiladi: ilovada hech qanday qat'iy rang yozilmaydi.
    const background = getComputedStyle(globalThis.document.documentElement)
      .getPropertyValue('--bg-base')
      .trim();
    if (background !== '') initTelegram(background);
  }, [theme]);

  useEffect(() => {
    /*
     * Bo'sh `initData` serverga YUBORILMAYDI.
     *
     * Ilgari SDK borligi "Telegram ichidamiz" deb hisoblanardi va
     * bo'sh satr yuborilib `400 VALIDATION_FAILED` olinardi. Har
     * brauzerda ochilish serverda xato yozuvi qoldirardi —
     * haqiqiy imzo hujumlari shu shovqin ichida ko'milib ketardi.
     *
     * Telegramdan TASHQARIDA yoki SDK YO'Q bo'lsa — urinish UMUMAN
     * bo'lmaydi, ilova to'g'ridan-to'g'ri mehmon sifatida ishlaydi.
     */
    if (telegramSession() === 'inside') {
      attemptLogin();
    } else {
      setGuest();
    }

    const generation = generationRef;
    return () => {
      // Avlodni bekor qilish — kech kelgan javob endi hech kimga
      // tegishli emas.
      generation.current++;
      abortCurrent();

      /*
       * FAIL-CLOSED unmount: `Bootstrap` o'chganda global sessiya
       * "authenticated" bo'lib QOLIB KETMASLIGI kerak — aks holda
       * keyingi o'rnatishning BIRINCHI render'i (o'zining effekti
       * ishga tushishidan OLDIN) eski holatni o'qib, o'z handshake'i
       * tugamasdan turib shaxsiy so'rovlarni yoqib yuborardi.
       *
       * Token va holat SINXRON tozalanadi — keyingi o'rnatish
       * darhol `canOrder() === false` ko'radi. Kesh tozalash esa
       * ASINXRON va sessiya HOLATIGA tegmaydi, shuning uchun bu
       * eski nusxaning kechikkan davomi yangi nusxaning
       * `authenticated`/`initializing` holatini bosib qololmaydi.
       */
      setTokens(null);
      setInitializing();
      void clearPrivateQueries(queryClient);
    };
    // `attemptLogin` barqaror (`useCallback`) — bu effekt faqat
    // o'rnatishda ishga tushadi, `queryClient` almashsa emas.
  }, [attemptLogin, abortCurrent, queryClient]);

  useEffect(() => {
    /*
     * Terminal `401` — yangilash kaliti yo'q, yangilash muvaffaqiyatsiz
     * yoki ikkinchi marta ham `401`. Joriy handshake avlodi bekor
     * qilinadi: keyinroq kelgan muvaffaqiyatli javob bu holatni endi
     * bosib qololmaydi.
     */
    setUnauthenticatedHandler(() => {
      const generation = ++generationRef.current;
      abortCurrent();
      setTokens(null);
      void clearPrivateQueries(queryClient).then(() => {
        if (generationRef.current !== generation) return;
        setFailed();
      });
    });

    return () => {
      setUnauthenticatedHandler(null);
    };
  }, [abortCurrent, queryClient]);

  return <Shell onRetryLogin={attemptLogin} />;
}

export function App(): ReactNode {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        {/*
          Bildirishnoma SettingsProvider ICHIDA — xabar matni tilga
          bog'liq va uni chaqiruvchi ekran `t()` orqali beradi.
        */}
        <ToastProvider>
          <RegionProvider>
            <NavigationProvider>
              <Bootstrap />
            </NavigationProvider>
          </RegionProvider>
        </ToastProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
}
